import { getAssetUrl } from '@lighthouse/assets'
import { useContainerBlockContext, useCustomViewBlockContext } from '@lighthouse/block'
import { TabsBlock } from '@lighthouse/block/src/blocks'
import { ALIGNMENT, type BlockAbstract, EVENT_TYPE, type FilterFormType, type TabsBlockAbstract, VISIBILITY_STATUS } from '@lighthouse/core'
import {
    getWithScopeIdAndRecordIdAndColumnId,
    TEXT_FONT_NORMAL,
    useActionRunningLoadings,
    useAppContainerContext,
    useAtomData
} from '@lighthouse/shared'
import { safeToNumber } from '@lighthouse/tools'
import equal from 'fast-deep-equal'
import produce from 'immer'
import React, { memo, useCallback, useEffect, useMemo } from 'react'

import { applicationSettingAtom } from '@/atoms/application/state'
import { useActionTrigger } from '@/hooks/useActionTrigger'
import { useParseVariableWithSubscription } from '@/hooks/useParseVariable'
import { useVisibilityFilter } from '@/hooks/useVisibilityFilter'

interface TabsControllerProps extends Omit<React.ComponentPropsWithoutRef<'div'>, 'onChange'> {
    blockData: TabsBlockAbstract
    onBlockChange?: (values: BlockAbstract, origin: BlockAbstract) => Promise<void> | void
}

const TabsController = ({ blockData, ...rest }: TabsControllerProps) => {
    const { variantMap, variant, breakPoint, baseList, defaultTabIndex } = blockData.config
    const { font } = breakPoint
    const { scope } = useContainerBlockContext()

    const parseVariable2Field = useParseVariableWithSubscription(defaultTabIndex)

    const defaultIndex = useMemo(() => {
        if (defaultTabIndex) {
            const index = safeToNumber(parseVariable2Field(defaultTabIndex)?.value) ?? 0
            if (index > baseList.length) {
                return baseList.length - 1
            }
            if (index < 1) {
                return 0
            }
            return index - 1
        }
        return 0
    }, [baseList.length, defaultTabIndex, parseVariable2Field])

    const { scale } = useAppContainerContext()
    const { record } = useCustomViewBlockContext()

    const usedFilters = useMemo(() => {
        const filters = baseList.map(item => item.visibilityFilter).filter(Boolean) as FilterFormType[]
        if (filters.length === 0) {
            return
        }

        return filters
    }, [baseList])
    const visibilityFilter = useVisibilityFilter(usedFilters)

    const visibleList = useMemo(
        () =>
            baseList.filter(item => {
                const isVisible = visibilityFilter({
                    visibility: {
                        visibilityFilter: item.visibilityFilter,
                        visible: 'FILTER'
                    }
                })
                return isVisible === VISIBILITY_STATUS.MOUNT
            }),
        [baseList, visibilityFilter]
    )

    const { handleActionListTrigger } = useActionTrigger(blockData.id)
    const { handleActionTriggerWithLoading } = useActionRunningLoadings()

    const handleTabsChange = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>, id: string) => {
            const actorEvent = baseList.find(item => item.id === id)?.eventList?.find(ev => ev.type === EVENT_TYPE.CLICK)
            if (!actorEvent) {
                return
            }
            const uniqueId = getWithScopeIdAndRecordIdAndColumnId(id, scope, record?.id)
            handleActionTriggerWithLoading({
                id: uniqueId,
                actorEvent,
                trigger: (id, actorEvent) => handleActionListTrigger(id, actorEvent, { viewRecord: record })
            })
        },
        [baseList, handleActionListTrigger, handleActionTriggerWithLoading, record, scope]
    )

    /** **************************** 文本样式字体 start **************************** */
    const textPresets = useAtomData(
        applicationSettingAtom,
        useCallback(s => s?.theme.textPresetList ?? [], [])
    )

    const mergedBlockData = useMemo(() => {
        const advance = font?.advance
        if (advance) {
            const advanceOption = textPresets.find(item => item.id === advance)
            if (advanceOption) {
                const { fontFamily, fontSize, letterSpacing, lineClamp, lineHeight } = advanceOption
                return produce(blockData, draft => {
                    draft.config.baseList = visibleList
                    draft.config.breakPoint.font = {
                        ...draft.config.breakPoint.font,
                        align: draft.config.breakPoint.font?.align || ALIGNMENT.left,
                        advance,
                        fontFamily,
                        fontSize,
                        letterSpacing,
                        lineClamp,
                        lineHeight
                    }
                })
            }
        }

        return produce(blockData, draft => {
            draft.config.baseList = visibleList
        })
    }, [blockData, font?.advance, textPresets, visibleList])

    useEffect(() => {
        const ff = mergedBlockData.config.breakPoint.font?.fontFamily
        if (!ff || ff === TEXT_FONT_NORMAL) {
            return
        }
        document.fonts.ready
            .then(async ffs => {
                const fontFaceList = [...ffs.values()]
                const isLoaded = fontFaceList.some(item => item.family === ff)
                if (!isLoaded) {
                    const fontUrl = `url(${getAssetUrl('font', `${ff}.woff2`)})`
                    const newFont = new FontFace(ff, fontUrl)
                    newFont._snapdomSrc = `${getAssetUrl('font', `${ff}.woff2`)}`
                    await newFont.load()
                    ffs.add(newFont)
                }
            })

            .catch(console.warn)
    }, [mergedBlockData.config.breakPoint.font?.fontFamily])
    /** **************************** 文本样式字体 end **************************** */

    return (
        <TabsBlock
            blockData={mergedBlockData}
            scale={scale}
            value={undefined}
            defaultIndex={defaultIndex}
            onClickTab={handleTabsChange}
            {...rest}
        />
    )
}

export default memo(TabsController, equal)
